<template>
  <div class="relative" @mouseleave="isOpen = false">
    <button
      role="combobox"
      :aria-activedescendant="focusedOptionId"
      aria-autocomplete="none"
      aria-controls="ratings-filter-options"
      :aria-expanded="isOpen"
      aria-label="Select rating"
      class="mr-2 w-full rounded-full border border-gray-light hover:border-black text-left text-sm h-9 pl-4"
      @click="open"
      @focusout="close"
      @keydown="handleKeyPress"
    >
      <span class="flex justify-between">
        <span v-if="selectedRating === 'all'" class="text-sm">Rating</span>
        <span v-else class="flex items-center">
          <span v-for="i in 5" :key="i" class="w-[18px] h-[18px] mr-2">
            <img
              :src="i <= parseInt(selectedRating) ? '/images/star-full-bw.svg' : '/images/star-empty-bw.svg'"
              width="18"
              height="18"
              alt="star"
            />
          </span>
        </span>
        <img src="@/assets/arrow.svg" class="w-2 mr-3" width="12" height="12" alt="dropdown arrow" />
      </span>
    </button>

    <div
      v-show="isOpen"
      id="ratings-filter-options"
      role="listbox"
      class="absolute w-full z-10 p-3 left-0 bg-white shadow-lg"
    >
      <div
        id="rating-option-0"
        class="flex justify-between cursor-pointer px-2 h-9 items-center text-sm hover:bg-gray-lighter"
        :class="{ 'outline outline-primary': focusIndex === 0 }"
        role="option"
        :aria-selected="selectedRating === 'all'"
        @click="selectRating('all')"
      >
        <span>All ratings</span>
        <img
          v-if="selectedRating === 'all'"
          src="@/assets/checkmarkThin.svg"
          class="w-3"
          width="12"
          height="12"
          alt="checkmark"
        />
      </div>
      <div
        v-for="(rating, index) in 5"
        :id="`rating-option-${index + 1}`"
        :key="6 - rating"
        role="option"
        :aria-selected="selectedRating === (6 - rating).toString()"
        class="flex justify-between cursor-pointer px-2 h-9 items-center text-sm hover:bg-gray-lighter"
        :class="{ 'outline outline-primary': focusIndex === index + 1 }"
        tabindex="-1"
        @click="selectRating((6 - rating).toString())"
      >
        <div class="flex items-center">
          <div v-for="i in 5" :key="`${rating}-${i}`" class="w-[18px] h-[18px] mr-2">
            <img
              :src="i <= 6 - rating ? '/images/star-full-bw.svg' : '/images/star-empty-bw.svg'"
              width="18"
              height="18"
              alt="star"
            />
          </div>
        </div>
        <img
          v-if="selectedRating === (6 - rating).toString()"
          src="@/assets/checkmarkThin.svg"
          class="w-3"
          width="12"
          height="12"
          alt="checkmark"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const { selectedRating } = defineProps<{
  selectedRating: string
}>()

const emit = defineEmits(['onRatingChange'])

const isOpen = ref(false)
const focusIndex = ref<number | null>(null)

const focusedOptionId = computed(() => {
  if (focusIndex.value === null || !isOpen.value) return
  return `rating-option-${focusIndex.value}`
})

function selectRating(value: string) {
  close()
  emit('onRatingChange', value)
}

function open() {
  isOpen.value = true
  focusIndex.value = 0
}

function close() {
  isOpen.value = false
  focusIndex.value = null
}

function handleKeyPress(event: KeyboardEvent) {
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) {
      open()
    } else {
      focusIndex.value = focusIndex.value === null || focusIndex.value === 5 ? 0 : focusIndex.value + 1
    }
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!isOpen.value) {
      isOpen.value = true
      focusIndex.value = 5
    } else {
      focusIndex.value = focusIndex.value === null || focusIndex.value === 0 ? 5 : focusIndex.value - 1
    }
  } else if (event.key === 'Enter') {
    event.preventDefault()
    if (isOpen.value && focusIndex.value !== null)
      selectRating(focusIndex.value === 0 ? 'all' : (6 - focusIndex.value).toString())
    else open()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}
</script>
